import type { ReactNode, RefObject } from 'react';
import styled, { css } from 'styled-components';

import { Link } from '../Button/Link';
import { Title } from './Title/Title';
import { MetaList } from './MetaList/MetaList';

export type Variants = 'DEFAULT' | 'SMALL' | 'XS';

export interface InfoTitleProps {
  title: string | ReactNode;
  description?: string | ReactNode;
  illustration?: string;
  icon?: ReactNode;
  listItems?: (string | undefined | ReactNode)[] | ReactNode;
  className?: string;
  shouldTruncate?: boolean;
  withH1Tag?: boolean;
  minHeight?: boolean;
  variant?: Variants;
  flip?: boolean;
  linkHref?: string;
  linkText?: string;
  disabled?: boolean;
  dataTestId?: string;
  metaListRef?: RefObject<HTMLUListElement>;
}

export const InfoTitleContainer = styled.div<{
  variant: Variants;
  minHeight: boolean;
  shouldTruncate: boolean;
}>`
  ${({ variant, minHeight }) =>
    minHeight &&
    css`
      min-height: ${variant === 'SMALL'
        ? '40px'
        : variant === 'XS'
        ? '16px'
        : '48px'};
    `}
  display: flex;
  align-items: center;
  width: 100%;
  ${({ shouldTruncate }) => shouldTruncate && `min-width: 0;`};
`;

export const ImageContainer = styled.div<{ variant: Variants }>`
  flex: 0 0 auto;
  margin-right: ${({ theme, variant }) =>
    variant === 'DEFAULT' ? theme.spacing.M16 : theme.spacing.S8};
  width: ${({ variant }) => (variant === 'XS' ? '20px' : '40px')};
  ${({ variant }) => variant === 'XS' && 'height: 20px'};

  img {
    width: 100%;
    height: auto;
  }
`;

export const IconContainer = styled.div<{ variant: Variants }>`
  margin-right: ${({ theme, variant }) =>
    variant === 'DEFAULT' ? theme.spacing.M16 : theme.spacing.S8};
`;

const Description = styled.p<{ variant: Variants; disabled?: boolean }>`
  display: block;
  margin: 0;
  ${({ theme, variant }) =>
    variant === 'SMALL' || variant === 'XS'
      ? theme.fontSize.B12
      : theme.fontSize.M14};
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.GREY_DARK : theme.colors.GREY_DARK};
`;

export const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
`;

const SLink = styled(Link)`
  ${({ theme }) => theme.fontSize.B12};
`;

/**
 * InfoTitle is a versatile component for displaying structured information with a title,
 * optional description, illustration/icon, metadata list, and action link.
 *
 * The component automatically handles different content types for listItems:
 * - listItems as an Array are rendered as MetaList components, ReactNode in listItems is rendered as-is for custom content
 * Illustration and icon are mutually exclusive:
 * - Illustration (image URL) or icon (ReactNode) can be provided for left-side visual, only define one of these props
 *
 * @param title - Main title content (string or ReactNode)
 * @param description - Optional description below the title
 * @param illustration - URL for illustration image on the left
 * @param icon - Icon component on the left
 * @param listItems - Array for MetaList or ReactNode for custom content
 * @param className - Additional CSS classes
 * @param shouldTruncate - Truncate long title text with ellipsis
 * @param withH1Tag - Use h1 tag for title (SEO/accessibility)
 * @param variant - Size variant: 'DEFAULT', 'SMALL', or 'XS'
 * @param minHeight - Apply minimum height for consistent layout
 * @param flip - Apply inverted color styling
 * @param linkHref - URL for bottom action link
 * @param linkText - Text for bottom action link
 * @param disabled - Apply disabled (grayed out) styling
 * @param dataTestId - Test identifier for automation
 * @param metaListRef - Ref for MetaList component access
 *
 * @returns React functional component
 */
function InfoTitle({
  title,
  description,
  illustration,
  icon,
  listItems,
  className,
  shouldTruncate = false,
  withH1Tag = false,
  variant = 'DEFAULT',
  minHeight = true,
  flip = false,
  linkHref,
  linkText,
  disabled,
  dataTestId,
  metaListRef,
}: InfoTitleProps) {
  return (
    <InfoTitleContainer
      className={className}
      variant={variant}
      minHeight={minHeight}
      shouldTruncate={shouldTruncate}
      data-testid={dataTestId}
    >
      {illustration && (
        <ImageContainer variant={variant}>
          <img src={illustration} alt={`${title} image`} />
        </ImageContainer>
      )}
      {icon && <IconContainer variant={variant}>{icon}</IconContainer>}
      <Content>
        <Title
          as={withH1Tag ? 'h1' : 'p'}
          shouldTruncate={shouldTruncate}
          variant={variant}
          flip={flip}
          disabled={disabled}
        >
          {title}
        </Title>
        {!!description && (
          <Description variant={variant} disabled={disabled}>
            {description}
          </Description>
        )}
        {Array.isArray(listItems) ? (
          <MetaList listItems={listItems} variant={variant} ref={metaListRef} />
        ) : (
          listItems
        )}
        {linkHref && linkText && (
          <SLink ofType="SECONDARY" href={linkHref}>
            {linkText}
          </SLink>
        )}
      </Content>
    </InfoTitleContainer>
  );
}

export { InfoTitle };
